import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, CheckCircle, Calculator, ChevronDown, Utensils, Save, RefreshCw, Info, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { FOOD_DATABASE } from '../data/foodDatabase';
import {
  divideMacrosByMeals,
  calculateProteinPortion,
  calculateCarbPortion,
  calculateSmartFat,
  calculateMealPortions,
  getFoodById,
  calculatePortionForSelect,
  filterFoodsByPreferences,
  filterFoodsByDietType,
  roundGramsForDisplay
} from '../utils/nutritionCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NutritionForm = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [canEditAgain, setCanEditAgain] = useState(false);
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState({});
  
  // Estado para rastrear grasa inteligente por comida
  const [mealCalculations, setMealCalculations] = useState({});

  const [formData, setFormData] = useState({
    nombre_completo: '',
    correo_electronico: '',
    sexo: 'mujer',
    edad: '',
    estatura: '',
    peso: '',
    nivel_actividad: 'moderado',
    objetivo: 'mantener',
    alergias: '',
    alimentos_no_gustan: '',
    lesiones_impedimentos: '',
    numero_comidas: '3',
    tipo_alimentacion: 'ninguna'
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data);

      setFormData(prev => ({
        ...prev,
        nombre_completo: userRes.data.name || '',
        correo_electronico: userRes.data.email || ''
      }));

      const formRes = await axios.get(`${BACKEND_URL}/api/nutrition-form/my-submission`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (formRes.data) {
        setExistingForm(formRes.data);
        if (formRes.data.nutrition_plan) {
          setNutritionPlan(formRes.data.nutrition_plan);
        }
        if (formRes.data.selected_foods) {
          setSelectedFoods(formRes.data.selected_foods);
        }
        // Verificar si puede editar de nuevo (después de 30 días)
        const createdAt = new Date(formRes.data.created_at);
        const now = new Date();
        const diffDays = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
        setCanEditAgain(diffDays >= 30);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Calcular GEB usando Harris-Benedict
  const calculateHarrisBenedict = (peso, estatura, edad, sexo) => {
    if (sexo === 'hombre') {
      return 66.5 + (13.75 * peso) + (5.003 * estatura) - (6.755 * edad);
    } else {
      return 655.1 + (9.563 * peso) + (1.850 * estatura) - (4.676 * edad);
    }
  };

  const getActivityFactor = (nivel) => {
    const factors = {
      sedentario: 1.2,
      ligero: 1.375,
      moderado: 1.55,
      activo: 1.725,
      muy_activo: 1.9
    };
    return factors[nivel] || 1.55;
  };

  const getObjectiveAdjustment = (objetivo) => {
    const adjustments = {
      perder: -500,
      mantener: 0,
      ganar: 300
    };
    return adjustments[objetivo] || 0;
  };

  // Filtrar alimentos según preferencias
  const filterFoods = (foods) => {
    const byPreferences = filterFoodsByPreferences(foods, formData.alergias, formData.alimentos_no_gustan);
    return filterFoodsByDietType(byPreferences, formData.tipo_alimentacion);
  };

  // Manejar selección de alimento y recalcular grasa inteligente
  const handleFoodSelect = (mealIndex, category, foodId) => {
    setSelectedFoods(prev => ({
      ...prev,
      [`meal_${mealIndex}_${category}`]: foodId
    }));
  };

  // Calcular la grasa inteligente para una comida cuando cambia la proteína
  const calculateMealFatRequirement = (mealIndex, proteinFoodId, targetFat) => {
    const proteinFood = getFoodById('proteina', proteinFoodId);
    if (!proteinFood) return { needsAdditionalFat: true, fatFromProtein: 0 };
    
    const plan = nutritionPlan || existingForm?.nutrition_plan;
    if (!plan) return { needsAdditionalFat: true, fatFromProtein: 0 };
    
    const proteinPerMeal = Math.round(plan.macros.proteinas_g / plan.comidas_por_dia);
    const proteinCalc = calculateProteinPortion(proteinFood, proteinPerMeal);
    
    return calculateSmartFat(targetFat, proteinCalc.fatContributed);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_completo || !formData.correo_electronico || !formData.edad || 
        !formData.estatura || !formData.peso || !formData.numero_comidas) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('session_token');

    try {
      const geb = calculateHarrisBenedict(
        parseFloat(formData.peso),
        parseFloat(formData.estatura),
        parseInt(formData.edad),
        formData.sexo
      );
      
      const activityFactor = getActivityFactor(formData.nivel_actividad);
      const tdee = geb * activityFactor;
      const objectiveAdjustment = getObjectiveAdjustment(formData.objetivo);
      const caloriasDiarias = Math.round(tdee + objectiveAdjustment);

      const proteinas = Math.round((caloriasDiarias * 0.30) / 4);
      const carbohidratos = Math.round((caloriasDiarias * 0.40) / 4);
      const grasas = Math.round((caloriasDiarias * 0.30) / 9);

      const numComidas = parseInt(formData.numero_comidas);
      const caloriasPorComida = Math.round(caloriasDiarias / numComidas);

      const macros = {
        proteinas_g: proteinas,
        carbohidratos_g: carbohidratos,
        grasas_g: grasas
      };

      const nutritionPlanData = {
        geb: Math.round(geb),
        tdee: Math.round(tdee),
        calorias_diarias: caloriasDiarias,
        macros,
        comidas_por_dia: numComidas,
        calorias_por_comida: caloriasPorComida,
        macros_por_comida: {
          proteinas: Math.round(proteinas / numComidas),
          carbohidratos: Math.round(carbohidratos / numComidas),
          grasas: Math.round(grasas / numComidas)
        }
      };

      const dataToSend = {
        ...formData,
        nutrition_plan: nutritionPlanData,
        selected_foods: selectedFoods
      };

      await axios.post(`${BACKEND_URL}/api/nutrition-form/submit`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('¡Información guardada! Ahora puedes armar tu plan.');
      setNutritionPlan(nutritionPlanData);
      setShowPlanBuilder(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setSubmitting(false);
    }
  };

  const savePlan = async () => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${BACKEND_URL}/api/nutrition-form/submit`, {
        ...formData,
        nutrition_plan: nutritionPlan,
        selected_foods: selectedFoods
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Plan guardado exitosamente!');
    } catch (error) {
      toast.error('Error al guardar el plan');
    }
  };

  const resetForm = () => {
    setExistingForm(null);
    setNutritionPlan(null);
    setShowPlanBuilder(false);
    setSelectedFoods({});
    setMealCalculations({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Componente para cada selector de comida con grasa inteligente
  const MealFoodSelectors = ({ mealIndex, plan, hasFruit, savedFoods, canEdit }) => {
    const proteinPerMeal = Math.round(plan.macros.proteinas_g / plan.comidas_por_dia);
    const carbsPerMeal = Math.round(plan.macros.carbohidratos_g / plan.comidas_por_dia);
    const fatsPerMeal = Math.round(plan.macros.grasas_g / plan.comidas_por_dia);
    
    // Obtener alimentos seleccionados para esta comida
    const selectedProteinId = selectedFoods[`meal_${mealIndex}_proteina`] || savedFoods[`meal_${mealIndex}_proteina`];
    const selectedCarbId = selectedFoods[`meal_${mealIndex}_carbohidrato`] || savedFoods[`meal_${mealIndex}_carbohidrato`];
    const selectedFatId = selectedFoods[`meal_${mealIndex}_grasa`] || savedFoods[`meal_${mealIndex}_grasa`];
    const selectedFruitId = selectedFoods[`meal_${mealIndex}_fruta`] || savedFoods[`meal_${mealIndex}_fruta`];
    
    // Obtener los alimentos de la DB
    const proteinFood = getFoodById('proteina', selectedProteinId);
    const carbFood = getFoodById('carbohidrato', selectedCarbId);
    const fatFood = getFoodById('grasa', selectedFatId);
    const fruitFood = getFoodById('fruta', selectedFruitId);
    
    // Calcular grasa inteligente
    let smartFatResult = { needsAdditionalFat: true, grams: 0, message: null };
    let proteinGrams = 0;
    let fatFromProtein = 0;
    
    if (proteinFood) {
      const proteinCalc = calculateProteinPortion(proteinFood, proteinPerMeal);
      proteinGrams = proteinCalc.grams;
      fatFromProtein = proteinCalc.fatContributed;
      smartFatResult = calculateSmartFat(fatsPerMeal, fatFromProtein, fatFood);
    }
    
    // Calcular carbohidratos considerando fruta
    let fruitCarbs = 0;
    let fruitGrams = 0;
    if (hasFruit && fruitFood) {
      // Fruta aporta ~15g de carbohidratos
      const fruitPortion = calculatePortionForSelect(fruitFood, 15, 'carbohidratos');
      fruitGrams = fruitPortion.displayGrams;
      fruitCarbs = 15;
    }
    
    const adjustedCarbs = Math.max(carbsPerMeal - fruitCarbs, 5);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Proteína */}
        <div className="bg-blue-50 rounded-xl p-4">
          <label className="block text-sm font-bold text-blue-700 mb-2">
            🥩 Proteína (Meta: {proteinPerMeal}g)
          </label>
          {!canEdit ? (
            proteinFood ? (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-blue-200">
                <div className="font-bold text-blue-800">{proteinFood.nombre}</div>
                <div className="text-sm text-blue-600 mt-1 font-medium">
                  {roundGramsForDisplay(proteinGrams)}g ({proteinFood.medicion})
                </div>
                <div className="text-xs text-blue-500 mt-1">
                  {proteinFood.proteinas}g prot/100g • Aporta ~{Math.round(fatFromProtein)}g grasa
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-blue-200 text-zinc-400">No seleccionado</div>
            )
          ) : (
            <select
              value={selectedProteinId || ''}
              onChange={(e) => handleFoodSelect(mealIndex, 'proteina', e.target.value)}
              className="w-full p-3 border-2 border-blue-200 rounded-xl bg-zinc-900 focus:border-blue-400 focus:outline-none text-white"
              data-testid={`meal-${mealIndex}-proteina-select`}
            >
              <option value="">Selecciona una proteína...</option>
              {filterFoods(FOOD_DATABASE.proteinas).map(food => {
                const portion = calculatePortionForSelect(food, proteinPerMeal, 'proteinas');
                return (
                  <option key={food.id} value={food.id}>
                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Carbohidrato */}
        <div className="bg-amber-50 rounded-xl p-4">
          <label className="block text-sm font-bold text-amber-700 mb-2">
            🍚 Carbohidrato (Meta: {adjustedCarbs}g{hasFruit ? ' + fruta' : ''})
          </label>
          {!canEdit ? (
            carbFood ? (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-amber-200">
                <div className="font-bold text-amber-800">{carbFood.nombre}</div>
                <div className="text-sm text-amber-600 mt-1 font-medium">
                  {(() => {
                    const portion = calculatePortionForSelect(carbFood, adjustedCarbs, 'carbohidratos');
                    return `${portion.displayGrams}g (${carbFood.medicion})`;
                  })()}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-amber-200 text-zinc-400">No seleccionado</div>
            )
          ) : (
            <select
              value={selectedCarbId || ''}
              onChange={(e) => handleFoodSelect(mealIndex, 'carbohidrato', e.target.value)}
              className="w-full p-3 border-2 border-amber-200 rounded-xl bg-zinc-900 focus:border-amber-400 focus:outline-none text-white"
              data-testid={`meal-${mealIndex}-carbohidrato-select`}
            >
              <option value="">Selecciona un carbohidrato...</option>
              {filterFoods(FOOD_DATABASE.carbohidratos).map(food => {
                const portion = calculatePortionForSelect(food, adjustedCarbs, 'carbohidratos');
                return (
                  <option key={food.id} value={food.id}>
                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Grasa Inteligente */}
        <div className={`rounded-xl p-4 ${smartFatResult.needsAdditionalFat ? 'bg-green-50' : 'bg-zinc-100'}`}>
          <label className="block text-sm font-bold text-green-700 mb-2">
            🥑 Grasa saludable 
            {smartFatResult.needsAdditionalFat 
              ? ` (Faltante: ${Math.round(smartFatResult.remainingFat || fatsPerMeal)}g)`
              : ' ✓'}
          </label>
          
          {!smartFatResult.needsAdditionalFat ? (
            <div className="p-3 bg-green-100 rounded-xl border-2 border-green-300">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">No necesitas grasa adicional</span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                La proteína seleccionada ya aporta suficiente grasa ({Math.round(fatFromProtein)}g)
              </p>
            </div>
          ) : !canEdit ? (
            fatFood ? (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-green-200">
                <div className="font-bold text-green-800">{fatFood.nombre}</div>
                <div className="text-sm text-green-600 mt-1 font-medium">
                  {smartFatResult.grams}g
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-900 rounded-xl border-2 border-green-200 text-zinc-400">No seleccionado</div>
            )
          ) : (
            <>
              <select
                value={selectedFatId || ''}
                onChange={(e) => handleFoodSelect(mealIndex, 'grasa', e.target.value)}
                className="w-full p-3 border-2 border-green-200 rounded-xl bg-zinc-900 focus:border-green-400 focus:outline-none text-white"
                data-testid={`meal-${mealIndex}-grasa-select`}
              >
                <option value="">Selecciona una grasa...</option>
                {filterFoods(FOOD_DATABASE.grasas).map(food => {
                  const neededFat = smartFatResult.remainingFat || fatsPerMeal;
                  const portion = calculatePortionForSelect(food, neededFat, 'grasas');
                  return (
                    <option key={food.id} value={food.id}>
                      {food.nombre} - {Math.round(portion.grams)}g
                    </option>
                  );
                })}
              </select>
              {proteinFood && (
                <p className="text-xs text-green-600 mt-2">
                  💡 La proteína aporta {Math.round(fatFromProtein)}g de grasa. Necesitas {Math.round(smartFatResult.remainingFat || 0)}g adicionales.
                </p>
              )}
            </>
          )}
        </div>

        {/* Fruta - Solo en comida 1 y Post Entreno */}
        {hasFruit && (
          <div className="bg-purple-50 rounded-xl p-4">
            <label className="block text-sm font-bold text-purple-700 mb-2">
              🍎 Fruta (~15g carbos)
            </label>
            {!canEdit ? (
              fruitFood ? (
                <div className="p-3 bg-zinc-900 rounded-xl border-2 border-purple-200">
                  <div className="font-bold text-purple-800">{fruitFood.nombre}</div>
                  <div className="text-sm text-purple-600 mt-1">
                    {fruitGrams}g ({fruitFood.medicion})
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-zinc-900 rounded-xl border-2 border-purple-200 text-zinc-400">No seleccionado</div>
              )
            ) : (
              <select
                value={selectedFruitId || ''}
                onChange={(e) => handleFoodSelect(mealIndex, 'fruta', e.target.value)}
                className="w-full p-3 border-2 border-purple-200 rounded-xl bg-zinc-900 focus:border-purple-400 focus:outline-none text-white"
                data-testid={`meal-${mealIndex}-fruta-select`}
              >
                <option value="">Selecciona una fruta...</option>
                {filterFoods(FOOD_DATABASE.frutas).map(food => {
                  const portion = calculatePortionForSelect(food, 15, 'carbohidratos');
                  return (
                    <option key={food.id} value={food.id}>
                      {food.nombre} - {portion.displayGrams}g ({food.carbohidratos}g carbs/100g)
                    </option>
                  );
                })}
              </select>
            )}
          </div>
        )}
      </div>
    );
  };

  // Mostrar el constructor de plan si ya tiene los cálculos
  if (showPlanBuilder || (existingForm && nutritionPlan)) {
    const plan = nutritionPlan || existingForm?.nutrition_plan;
    const savedFoods = existingForm?.selected_foods || selectedFoods;
    const hasSavedPlan = existingForm && Object.keys(savedFoods).length > 0;
    
    return (
      <div className="min-h-screen bg-[#09090b] py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center text-zinc-400 hover:text-pink-600 mb-4"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Dashboard
            </button>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2 text-white">
                  {hasSavedPlan ? 'Mi Plan Nutricional' : 'Crear Mi Plan Nutricional'}
                </h1>
                <p className="text-zinc-400">
                  {hasSavedPlan 
                    ? 'Este es tu plan de alimentación personalizado' 
                    : 'Selecciona los alimentos para cada comida'}
                </p>
              </div>
              {canEditAgain && (
                <Button 
                  onClick={resetForm}
                  variant="outline"
                  className="rounded-full"
                  data-testid="update-plan-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar mi plan
                </Button>
              )}
            </div>
            
            {/* Mensaje de plan bloqueado */}
            {hasSavedPlan && !canEditAgain && (
              <div className="mt-4 bg-amber-900/30 border border-amber-500/50 rounded-xl p-4">
                <p className="text-amber-300 text-sm">
                  <strong>📅 Tu plan está guardado.</strong> Podrás modificarlo nuevamente después de 30 días desde su creación.
                </p>
              </div>
            )}
          </div>

          {plan && (
            <div className="space-y-6">
              {/* Resumen de calorías */}
              <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-2xl p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Calculator className="w-6 h-6" />
                  Tu Requerimiento Calórico Diario
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-900/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.calorias_diarias}</div>
                    <div className="text-sm opacity-90">Calorías totales</div>
                  </div>
                  <div className="bg-zinc-900/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.proteinas_g}g</div>
                    <div className="text-sm opacity-90">Proteínas</div>
                  </div>
                  <div className="bg-zinc-900/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.carbohidratos_g}g</div>
                    <div className="text-sm opacity-90">Carbohidratos</div>
                  </div>
                  <div className="bg-zinc-900/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.grasas_g}g</div>
                    <div className="text-sm opacity-90">Grasas</div>
                  </div>
                </div>
                
                {/* División por comida */}
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm opacity-90 mb-2">
                    📊 División por comida ({plan.comidas_por_dia} comidas):
                  </p>
                  <div className="flex gap-4 text-sm">
                    <span>~{plan.macros_por_comida.proteinas}g proteína</span>
                    <span>•</span>
                    <span>~{plan.macros_por_comida.carbohidratos}g carbos</span>
                    <span>•</span>
                    <span>~{plan.macros_por_comida.grasas}g grasa</span>
                    <span>•</span>
                    <span>~{plan.calorias_por_comida} kcal</span>
                  </div>
                </div>
              </div>

              {/* ESTE SERÁ TU PLAN */}
              <div className="bg-zinc-900 rounded-2xl p-6 border-2 border-pink-300">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2 text-pink-600">
                  <Utensils className="w-7 h-7" />
                  {hasSavedPlan && !canEditAgain ? 'Mi Plan de Alimentación ✓' : hasSavedPlan ? 'Actualizar Mi Plan' : 'Este Será Tu Plan'}
                </h2>
                <p className="text-zinc-400 mb-6">
                  {hasSavedPlan && !canEditAgain
                    ? `Plan guardado con ${plan.comidas_por_dia} comidas al día. Estos son los alimentos que seleccionaste:`
                    : hasSavedPlan 
                      ? `Modifica los alimentos de tu plan de ${plan.comidas_por_dia} comidas.`
                      : `Selecciona un alimento de cada categoría para armar tus ${plan.comidas_por_dia} comidas del día.`}
                </p>
                
                {/* Nota sobre grasa inteligente */}
                <div className="bg-green-900/30 border border-green-500/50 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-300 text-sm font-medium">
                        💡 Sistema de Grasa Inteligente
                      </p>
                      <p className="text-green-400/80 text-xs mt-1">
                        El sistema calcula automáticamente si necesitas agregar grasa adicional. 
                        Si la proteína seleccionada ya aporta suficiente grasa, el selector de grasa se desactivará.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Comidas */}
                <div className="space-y-8">
                  {Array.from({ length: plan.comidas_por_dia }, (_, mealIndex) => {
                    const getMealName = (idx) => {
                      if (idx === 0) return 'Comida 1';
                      if (idx === 1) return 'Post Entreno';
                      return `Comida ${idx + 1}`;
                    };
                    
                    // Solo fruta en comida 1 (idx 0) y Post Entreno (idx 1)
                    const hasFruit = mealIndex === 0 || mealIndex === 1;
                    const caloriesPerMeal = Math.round(plan.calorias_diarias / plan.comidas_por_dia);
                    
                    return (
                      <div key={mealIndex} className="border-2 border-zinc-700 rounded-2xl p-6" data-testid={`meal-${mealIndex}-container`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 ${mealIndex === 1 ? 'bg-orange-100' : 'bg-pink-100'} rounded-full flex items-center justify-center`}>
                            <span className={`font-bold ${mealIndex === 1 ? 'text-orange-600' : 'text-pink-600'} text-lg`}>{mealIndex + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">{getMealName(mealIndex)}</h3>
                            <p className="text-sm text-zinc-500">
                              ~{caloriesPerMeal} kcal | 
                              P: {plan.macros_por_comida.proteinas}g | 
                              C: {plan.macros_por_comida.carbohidratos}g | 
                              G: {plan.macros_por_comida.grasas}g
                            </p>
                          </div>
                        </div>
                        
                        {/* Nota especial para Post Entreno */}
                        {mealIndex === 1 && (
                          <div className="bg-orange-900/30 border border-orange-500/50 rounded-xl p-3 mb-4">
                            <p className="text-sm text-orange-300">
                              <strong>💡 Comida flotante:</strong> Esta comida la mueves para después de entrenar, sin importar la hora en la que entrenes.
                            </p>
                          </div>
                        )}

                        <MealFoodSelectors 
                          mealIndex={mealIndex}
                          plan={plan}
                          hasFruit={hasFruit}
                          savedFoods={savedFoods}
                          canEdit={!hasSavedPlan || canEditAgain}
                        />

                        {/* Vegetales - siempre libres */}
                        <div className="mt-4 bg-emerald-900/30 border border-emerald-500/50 rounded-xl p-4">
                          <label className="block text-sm font-bold text-emerald-400 mb-2">
                            🥦 Vegetales (libre consumo)
                          </label>
                          <p className="text-emerald-300 text-sm">
                            Puedes agregar todos los vegetales que quieras. <strong>Sugerido: 100-200g</strong>
                          </p>
                          <p className="text-emerald-400/70 text-xs mt-1">
                            Los vegetales no cuentan en tus macros. ¡Agrégalos libremente!
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Botón guardar - solo mostrar si NO tiene plan guardado o SI puede editar */}
                {(!hasSavedPlan || canEditAgain) && (
                  <div className="mt-8">
                    <Button
                      onClick={savePlan}
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold text-lg"
                      data-testid="save-plan-btn"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      {hasSavedPlan ? 'Actualizar Mi Plan' : 'Guardar Mi Plan'}
                    </Button>
                    <p className="text-center text-sm text-zinc-500 mt-3">
                      Podrás actualizar tu plan nutricional después de 30 días
                    </p>
                  </div>
                )}
              </div>

              {/* Notas */}
              <div className="bg-amber-900/30 border border-amber-500/50 rounded-2xl p-6">
                <h3 className="font-bold text-amber-300 mb-2">📌 Notas Importantes</h3>
                <ul className="text-amber-400/90 text-sm space-y-2">
                  <li>• <strong>Proteínas:</strong> Los valores están en CRUDO (peso antes de cocinar)</li>
                  <li>• <strong>Carbohidratos:</strong> Arroz, pasta, papa, legumbres = COCIDO | Avena, crema de arroz = CRUDO (polvo)</li>
                  <li>• <strong>Frutas:</strong> Valores en CRUDO. Cuentan dentro de los carbohidratos de la comida</li>
                  <li>• <strong>Vegetales:</strong> Libre consumo. ¡Agrega todos los que quieras!</li>
                  <li>• <strong>Grasa Inteligente:</strong> El sistema calcula si necesitas agregar grasa adicional basándose en la grasa que aporta la proteína</li>
                  <li>• Bebe al menos 2 litros de agua diarios</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-zinc-400 hover:text-pink-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </button>
          
          <h1 className="text-3xl font-bold mb-2 text-white">Formulario de Ingreso</h1>
          <p className="text-zinc-400">Plan Nutricional Nivel 1 - Principiante</p>
        </div>

        {/* Nota informativa */}
        <div className="bg-pink-900/30 border border-pink-500/50 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Apple className="w-6 h-6 text-pink-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-pink-300 mb-2">NOTA IMPORTANTE</h3>
              <p className="text-pink-200/80 text-sm leading-relaxed">
                El objetivo de este plan es introducirte a un estilo de vida más saludable, por lo que 
                decidimos crearte un plan de entrenamiento de 4 días y un programa de nutrición para 
                mejorar tus hábitos y lograr una recomposición corporal.
              </p>
              <p className="text-pink-200/80 text-sm leading-relaxed mt-3">
                <strong>¿Sabías que por ser principiante es mucho más fácil para ti lograr cambios rápidamente?</strong> 
                {' '}Esto quiere decir que si llegaste aquí queriendo bajar tu grasita o lograr unas piernotas, 
                en unas cuantas semanas empezarás a disfrutar de esos resultados.
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-3xl p-6 border border-zinc-800 space-y-6" data-testid="nutrition-form">
          {/* Campos del formulario */}
          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              1. Nombre completo <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.nombre_completo}
              onChange={(e) => handleChange('nombre_completo', e.target.value)}
              placeholder="Tu nombre completo"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="nombre-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              2. Correo electrónico <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData.correo_electronico}
              onChange={(e) => handleChange('correo_electronico', e.target.value)}
              placeholder="tu@email.com"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              3. Sexo <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input
                  type="radio"
                  name="sexo"
                  value="mujer"
                  checked={formData.sexo === 'mujer'}
                  onChange={(e) => handleChange('sexo', e.target.value)}
                  className="w-4 h-4 text-pink-600"
                />
                <span>Mujer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input
                  type="radio"
                  name="sexo"
                  value="hombre"
                  checked={formData.sexo === 'hombre'}
                  onChange={(e) => handleChange('sexo', e.target.value)}
                  className="w-4 h-4 text-pink-600"
                />
                <span>Hombre</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              4. Edad <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={formData.edad}
              onChange={(e) => handleChange('edad', e.target.value)}
              placeholder="25"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
              min="15"
              max="100"
              required
              data-testid="edad-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              5. Estatura (cm) <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              value={formData.estatura}
              onChange={(e) => handleChange('estatura', e.target.value)}
              placeholder="165"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
              min="100"
              max="250"
              required
              data-testid="estatura-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              6. Peso (kg) <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              step="0.1"
              value={formData.peso}
              onChange={(e) => handleChange('peso', e.target.value)}
              placeholder="60"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
              min="30"
              max="300"
              required
              data-testid="peso-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              7. Nivel de actividad física <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.nivel_actividad}
              onChange={(e) => handleChange('nivel_actividad', e.target.value)}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
              required
              data-testid="actividad-select"
            >
              <option value="sedentario">Sedentario (poco o nada de ejercicio)</option>
              <option value="ligero">Ligero (ejercicio 1-3 días/semana)</option>
              <option value="moderado">Moderado (ejercicio 3-5 días/semana)</option>
              <option value="activo">Activo (ejercicio 6-7 días/semana)</option>
              <option value="muy_activo">Muy activo (atleta o trabajo físico intenso)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              8. Objetivo principal <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.objetivo}
              onChange={(e) => handleChange('objetivo', e.target.value)}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
              required
              data-testid="objetivo-select"
            >
              <option value="perder">Perder grasa (déficit calórico)</option>
              <option value="mantener">Mantener peso (recomposición)</option>
              <option value="ganar">Ganar masa muscular (superávit calórico)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              9. ¿Sigues algun tipo de alimentacion especial?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'ninguna', label: 'Ninguna', desc: 'Todos los alimentos' },
                { value: 'vegetariano', label: 'Vegetariano', desc: 'Sin carne ni pescado' },
                { value: 'vegano', label: 'Vegano estricto', desc: 'Solo alimentos vegetales' },
                { value: 'pescetariano', label: 'Pescetariano', desc: 'Sin carne roja ni pollo' }
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => handleChange('tipo_alimentacion', opt.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    formData.tipo_alimentacion === opt.value
                      ? 'border-green-500 bg-green-500/10 text-white'
                      : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-500'
                  }`}
                  data-testid={`diet-type-${opt.value}`}
                >
                  <p className="font-bold text-sm">{opt.label}</p>
                  <p className="text-xs opacity-70">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              10. Alergias (alimentos y medicamentos)
            </label>
            <textarea
              value={formData.alergias}
              onChange={(e) => handleChange('alergias', e.target.value)}
              placeholder="Describe tus alergias alimentarias o a medicamentos. Si no tienes, escribe 'Ninguna'"
              rows={3}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder-zinc-500"
              data-testid="alergias-textarea"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              11. Escriba los alimentos que NO le gusten
            </label>
            <textarea
              value={formData.alimentos_no_gustan}
              onChange={(e) => handleChange('alimentos_no_gustan', e.target.value)}
              placeholder="Ej: brócoli, pescado, hígado, etc. Separados por coma"
              rows={3}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder-zinc-500"
              data-testid="no-gustan-textarea"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              12. ¿Tienes alguna lesión o impedimento para realizar algún ejercicio?
            </label>
            <textarea
              value={formData.lesiones_impedimentos}
              onChange={(e) => handleChange('lesiones_impedimentos', e.target.value)}
              placeholder="Especifica cuáles lesiones o impedimentos tienes. Si no tienes, escribe 'Ninguno'"
              rows={3}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder-zinc-500"
              data-testid="lesiones-textarea"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              12. Número de comidas que deseas consumir al día <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.numero_comidas}
              onChange={(e) => handleChange('numero_comidas', e.target.value)}
              className="w-full px-4 py-2 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
              required
              data-testid="comidas-select"
            >
              <option value="3">3 comidas</option>
              <option value="4">4 comidas</option>
              <option value="5">5 comidas</option>
              <option value="6">6 comidas</option>
            </select>
            <p className="text-xs text-zinc-500 mt-1">
              Serán las mismas calorías, solo que serán repartidas en esta cantidad de comidas.
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold"
            data-testid="submit-btn"
          >
            {submitting ? (
              'Calculando...'
            ) : (
              <>
                <Calculator className="w-5 h-5 mr-2" />
                Calcular y Crear Mi Plan
              </>
            )}
          </Button>

          <p className="text-xs text-zinc-500 text-center">
            Al enviar, calcularemos tus calorías y podrás armar tu plan de comidas personalizado.
          </p>
        </form>
      </div>
    </div>
  );
};

export default NutritionForm;
